import React, { useEffect, useState } from 'react';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { isEmptyObject } from 'helpers/formatObject';
import {
  Card,
  Stack,
  Avatar,
  Button,
  Tooltip,
  CardMedia,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';

import {
  Gallery,
  NewDialog,
  MediaDeleter,
  MediaUploader,
  NewDialogTitle,
  NewDialogContent,
  NewDialogActions,
  DialogChildButton,
} from 'components';

const NewMediaPopup = ({ id, title, subheader, model, children, graph, collection }) => {
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [current, setCurrent] = useState([]);
  const { userToken } = useSelector((state) => state.auth);

  const [getCurrent, { loading }] = useLazyQuery(graph.current.query, {
    context: {
      serviceName: graph.current.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  const refetch = () => setRefresh((prev) => !prev);

  const handleModelMedia = async () => {
    try {
      const { data } = await getCurrent({
        variables: {
          ids: id,
        },
      });
      if (!isEmptyObject(data)) {
        setCurrent(data[graph.current.name].data[0]?.media || []);
      }
    } catch (error) {}
  };

  useEffect(() => {
    open && handleModelMedia();
  }, [open, refresh]);

  return (
    <>
      <DialogChildButton
        title="رسانه ها"
        onClick={onOpen}
        size="small"
        color="warning"
        children={children}
        icon={<ImageRoundedIcon fontSize="small" color="warning" />}
      />
      <NewDialog open={open} label="رسانه ها" onClose={onClose}>
        <NewDialogTitle title="رسانه ها" onClose={onClose} />
        <NewDialogContent isPopup>
          <Handler
            modelId={id}
            model={model}
            title={title}
            current={current}
            refetch={refetch}
            loading={loading}
            subheader={subheader}
          />
        </NewDialogContent>
        <NewDialogActions>
          <Tooltip title="تازه سازی">
            <IconButton sx={{ bgcolor: 'action.hover', color: '#fff' }} size="medium" onClick={refetch}>
              {loading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <RefreshRoundedIcon color="inherit" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Gallery model={model} modelId={id} refetch={refetch} />
          <MediaUploader model={model} modelId={id} refetch={refetch} collection={collection} />
        </NewDialogActions>
      </NewDialog>
    </>
  );
};

function Handler({ model, title, loading, current, modelId, refetch, subheader }) {
  if (loading) {
    return <Loading />;
  }
  if (!current.length) {
    return <NotFound title={title} subheader={subheader} />;
  }
  return <Content model={model} current={current} modelId={modelId} refetch={refetch} />;
}

function Loading() {
  return (
    <Stack flex={1} justifyContent="center" alignItems="center">
      <CircularProgress />
    </Stack>
  );
}

function NotFound({ title, subheader }) {
  const searchInGoogle = () => {
    window.open(`https://www.google.com/search?q=${title}+${subheader}`, '_blank');
  };
  return (
    <Stack flex={1} rowGap={2} alignItems="center" justifyContent="center" bgcolor="background.paper">
      <Avatar
        variant="rounded"
        sx={{
          width: 120,
          height: 120,
          borderRadius: 4,
        }}
        src="/assets/icons/glass/empty.svg"
      />
      <Typography variant="subtitle1">موردی یافت نشد!</Typography>
      <Button variant="outlined" onClick={searchInGoogle} sx={{ mt: 1, minWidth: 180 }}>
        جستجوی تصویر در گوگل
      </Button>
    </Stack>
  );
}

function Content({ current, model, modelId, refetch }) {
  return (
    <Stack rowGap={1} p={1}>
      {current.map((item, i) => {
        const { id, name, size, full_url, collection_name } = item;
        return (
          <Card key={i} variant="outlined" sx={{ display: 'flex' }}>
            <CardMedia alt={name} component="img" image={full_url} sx={{ width: 120 }} />
            <Stack flex={1} p={1} rowGap={1} alignItems="flex-end">
              <Typography variant="subtitle1">{name}</Typography>
              <Typography fontSize={14} variant="subtitle1" color="text.secondary">
                {`${Math.floor(size / 1000)} کیلوبایت`}
              </Typography>
              <MediaDeleter mediaId={id} model={model} refetch={refetch} modelId={modelId} />
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

export default NewMediaPopup;
